import { useMemo } from 'react';
import { isToday, isYesterday, format, parseISO } from 'date-fns';
import { EventCard } from './EventCard';
import { EmptyState } from '../common/EmptyState';
import type { ActivityEvent } from '../../types/activity';

interface EventFeedProps {
  events: ActivityEvent[];
  hasActiveFilters: boolean;
}

interface DayGroup {
  label: string;
  events: ActivityEvent[];
}

function getDayLabel(date: Date): string {
  if (isToday(date)) {
    return `Today \u2014 ${format(date, 'MMMM d, yyyy')}`;
  }
  if (isYesterday(date)) {
    return `Yesterday \u2014 ${format(date, 'MMMM d, yyyy')}`;
  }
  return `${format(date, 'EEEE')} \u2014 ${format(date, 'MMMM d, yyyy')}`;
}

function getDayKey(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

function groupEventsByDay(events: ActivityEvent[]): DayGroup[] {
  const grouped = new Map<string, { label: string; events: ActivityEvent[] }>();

  for (const event of events) {
    const date = parseISO(event.timestamp);
    const key = getDayKey(date);
    const existing = grouped.get(key);
    if (existing) {
      existing.events.push(event);
    } else {
      grouped.set(key, { label: getDayLabel(date), events: [event] });
    }
  }

  return Array.from(grouped.values());
}

export function EventFeed({ events, hasActiveFilters }: EventFeedProps) {
  const dayGroups = useMemo(() => groupEventsByDay(events), [events]);

  if (events.length === 0) {
    return (
      <EmptyState
        title="No events found"
        description={
          hasActiveFilters
            ? 'Try adjusting your filters to see more events.'
            : 'Events will appear here as jobs run and activity occurs.'
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      {dayGroups.map((group) => (
        <div key={group.label}>
          <h4 className="text-sm font-semibold text-text-secondary py-2 border-b border-border-subtle mb-3">
            {group.label}
          </h4>
          <div className="bg-bg-secondary rounded-lg border border-border-subtle">
            {group.events.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default EventFeed;
